/**
 * Account › Subscription — current plan + entry points to plans/portal.
 * Extracted from Account.tsx (P8). Beta-gated by the caller.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  getSubscription,
  openPortal,
  tierOfGroups,
  type SubscriptionDetails,
} from '../../agents/billingApi.ts';
import type { AuthUser } from '../../auth/AuthContext.ts';

interface Props {
  readonly user: AuthUser | null;
  readonly hidden?: boolean;
}

const fmtMoney = (amount: number, currency: string): string =>
  new Intl.NumberFormat(undefined, { style: 'currency', currency: currency.toUpperCase() }).format(
    amount / 100,
  );
const fmtDate = (iso: string): string =>
  new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });

/** Human label for the account's plan; admin/VIP read as full-access. */
function planLabelFor(isAdmin: boolean, tier: ReturnType<typeof tierOfGroups>): string {
  if (isAdmin) {
    return 'Admin — full access';
  }
  if (tier === 'vip') {
    return 'VIP — full access';
  }
  if (tier === null) {
    return 'Free';
  }
  return `${tier.charAt(0).toUpperCase()}${tier.slice(1)}`;
}

/** The renewal/amount/card/status list — only shown for paid Stripe tiers. */
function SubscriptionDetailsList({ details }: { readonly details: SubscriptionDetails }) {
  return (
    <dl className="om-account__dl">
      {details.amount !== null ? (
        <div>
          <dt>Price</dt>
          <dd>
            {fmtMoney(details.amount, details.currency)}
            {details.interval !== null ? ` / ${details.interval}` : ''}
          </dd>
        </div>
      ) : null}
      {details.renewsAt !== null ? (
        <div>
          <dt>{details.cancelAtPeriodEnd ? 'Cancels on' : 'Renews on'}</dt>
          <dd>{fmtDate(details.renewsAt)}</dd>
        </div>
      ) : null}
      {details.card !== null ? (
        <div>
          <dt>Payment</dt>
          <dd>
            {details.card.brand} ····{details.card.last4}
          </dd>
        </div>
      ) : null}
      {details.status === 'past_due' ? (
        <div>
          <dt>Status</dt>
          <dd className="om-account__warn">Payment past due</dd>
        </div>
      ) : null}
    </dl>
  );
}

export default function AccountSubscriptionSection({ user, hidden = false }: Props) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const groups = user?.groups ?? [];
  const tier = tierOfGroups(groups);
  // Admin and VIP are unlimited "full access" accounts with NO Stripe
  // subscription — they show a full-access badge and no billing buttons.
  // `admin` isn't a paid tier (tierOfGroups ignores it), so detect it here.
  const isAdmin = groups.includes('admin');
  const fullAccess = isAdmin || tier === 'vip';
  const [details, setDetails] = useState<SubscriptionDetails | null>(null);

  // Enrich the group-derived tier with Stripe billing details (renewal,
  // amount, card). Full-access (admin/VIP) and free accounts have no Stripe
  // subscription, so skip the fetch — only paid tiers have billing details.
  useEffect(() => {
    if (tier === null || fullAccess) {
      return;
    }
    let live = true;
    void getSubscription()
      .then((d) => {
        if (live) {
          setDetails(d);
        }
      })
      .catch(() => {
        /* non-fatal: the tier still shows from groups */
      });
    return () => {
      live = false;
    };
  }, [tier, fullAccess]);

  const manageBilling = () => {
    setBusy(true);
    setError(null);
    void openPortal()
      .then((url) => {
        if (url === null) {
          navigate('/pricing');
        } else {
          window.location.assign(url);
        }
      })
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : 'The billing portal could not be opened.');
        setBusy(false);
      });
  };

  const planLabel = planLabelFor(isAdmin, tier);
  // Full-access accounts share the VIP badge styling; paid tiers use their own.
  const badgeTier = fullAccess ? 'vip' : (tier ?? 'free');

  return (
    <section className="om-account__section" id="subscription" hidden={hidden}>
      <h2 className="om-account__h2">Subscription</h2>
      <div className="om-account__tier">
        <span className="om-account__tier-badge" data-tier={badgeTier}>
          {planLabel}
        </span>
        {details?.interval != null ? (
          <span className="om-account__tier-interval">
            {details.interval === 'year' ? 'annual' : 'monthly'}
          </span>
        ) : null}
      </div>
      {details !== null ? <SubscriptionDetailsList details={details} /> : null}
      {error !== null ? (
        <p className="om-account__error" role="alert">
          {error}
        </p>
      ) : null}
      {/* Admin + VIP are unlimited full-access accounts with no billing to
          manage — they get no subscription buttons at all. */}
      {fullAccess ? null : (
        <div className="om-account__sms-actions om-account__sms-actions--spaced">
          {tier === null ? (
            <Link to="/pricing" className="om-account__action">
              View plans
            </Link>
          ) : (
            <>
              <button
                type="button"
                className="om-account__action"
                disabled={busy}
                onClick={manageBilling}
              >
                {busy ? 'Opening…' : 'Manage subscription'}
              </button>
              <Link to="/pricing" className="om-account__action">
                Change plan
              </Link>
            </>
          )}
        </div>
      )}
      {fullAccess ? (
        <p className="om-account__channel-link">
          Your account has permanent full access — no subscription needed.
        </p>
      ) : null}
    </section>
  );
}
